import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../store';
import { useLightMode, useTaxMode } from '../utils';
import { PaymentsService } from '../api/PaymentsAPI';
import { Header } from '../components/Header/Header';
import { DateRangeFilter } from '../components/DateRangeFilter/DateRangeFilter';
import { AppointmentSelect } from '../components/AppointmentSelect/AppointmentSelect';
import s from '../styles/staff.module.css';

const money = (cents) =>
  `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// No `timeZone` option → formats in the viewer's own browser timezone.
const fmtDateTime = (v) =>
  v ? new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }) : '—';

// Render a list of strings stacked vertically (one per line), or a dash.
const stacked = (items) =>
  items && items.length
    ? items.map((t, i) => <div key={i}>{t}</div>)
    : '—';

// How the payment was taken, with the detail that matters for that method:
// the card brand for a Square charge, who owes it for a receivable, and who
// keyed it in for anything entered by hand.
function methodCell(p) {
  const title = p.paymentMethodTitle || (p.externalId ? 'Card at pickup' : 'Not set');
  const detail = p.billedTo
    || p.invoiceNumber
    || (!p.externalId && p.enteredByName ? `Entered by ${p.enteredByName}` : null)
    || (p.externalId && p.cardType ? p.cardType : null);
  return (
    <>
      {title}
      {detail && <span className={s.subCell}>{detail}</span>}
      {p.outstanding && <span className={s.subCell}>outstanding</span>}
    </>
  );
}

// No Square id means nobody's terminal produced it — a person typed it in.
const isManualEntry = (p) => !p.externalId;

const PAY_FILTERS = [
  { id: 'all',      label: 'All',          test: () => true },
  { id: 'review',   label: 'Needs review', test: (p) => p.link === 'review' },
  { id: 'unlinked', label: 'Unlinked',     test: (p) => p.link === 'unlinked' },
  { id: 'manual',   label: 'Manual',       test: (p) => p.link === 'manual' },
  { id: 'linked',   label: 'Linked',       test: (p) => p.link === 'linked' },
];

const LINK_STATE = {
  linked:   { label: 'Linked',   tone: 'pillPaid' },
  review:   { label: 'Review',   tone: 'pillManual' },
  unlinked: { label: 'Unlinked', tone: 'pillDue' },
  manual:   { label: 'Manual',   tone: 'pillNoCharge' },
};

// The appointment's own figure is a quote, stored pre-tax, so it follows the
// header switch. The payment columns beside it already break tax out on their
// own and are left exactly as they are.
const apptChip = (p, taxMode) => {
  const quote = taxMode === 'after'
    ? (p.suggestion?.owedCents ?? p.suggestion?.totalAmount)
    : p.suggestion?.totalAmount;
  if (p.link === 'linked')   return `Linked Appt — ${fmtDateTime(p.suggestion?.startAt)} · ${money(quote)}`;
  if (p.link === 'review')   return `Suggested Appt — ${fmtDateTime(p.suggestion?.startAt)} · ${money(quote)}`;
  if (p.link === 'manual')   return 'Manual entry';
  return 'Not linked to any appointment';
};

// How sure the match is. Shown beside the badge so a weak auto-link is visible
// rather than being presented as settled fact.
function linkCell(p) {
  const st = LINK_STATE[p.link] || LINK_STATE.unlinked;
  return (
    <>
      <span className={`${s.statusPill} ${s[st.tone]}`}>{st.label}</span>
      {p.confidence != null && <span className={s.linkConfidence}>{p.confidence}%</span>}
    </>
  );
}

export function TransactionsPage() {
  const { user } = useAuth();
  const [lightMode, toggleLightMode] = useLightMode();
  const [taxMode, setTaxMode] = useTaxMode();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';

  const [range, setRange] = useState({ start: '', end: '' });
  const onRangeChange = useCallback((r) => setRange(r), []);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['transactions', range.start, range.end],
    queryFn: () => PaymentsService.list(range.start, range.end),
    enabled: !!range.start && !!range.end,
    placeholderData: (prev) => prev,
  });
  const payments = data?.payments || [];

  const [filter, setFilter] = useState('all');
  const visiblePayments = useMemo(
    () => payments.filter((PAY_FILTERS.find((f) => f.id === filter) || PAY_FILTERS[0]).test),
    [payments, filter]);

  // Tiles describe the whole range, not the current filter.
  const tiles = useMemo(() => payments.reduce((t, p) => {
    if (p.link === 'linked')        t.linked += 1;
    else if (p.link === 'review')   t.review += 1;
    else if (p.link === 'manual') { t.manual += 1; if (p.needsVerification) t.unverified += 1; }
    else { t.unlinked += 1; t.unlinkedTips += p.tip || 0; }
    t.revenue += p.amount || 0;
    return t;
  }, { linked: 0, review: 0, unlinked: 0, unlinkedTips: 0, manual: 0, unverified: 0, revenue: 0 }), [payments]);

  const handleSetAppointment = useCallback(async (orderId, bookId) => {
    if (!orderId) return;
    try {
      await PaymentsService.setAppointment(orderId, bookId);
      await refetch();
      // Linking a payment takes it off the exception list.
      queryClient.invalidateQueries({ queryKey: ['attention'] });
    } catch (err) {
      console.error('Link appointment failed:', err?.error || err?.message || err);
    }
  }, [refetch, queryClient]);

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <Header lightMode={lightMode} toggleLightMode={toggleLightMode}
        taxMode={taxMode} onTaxModeChange={setTaxMode} />

      <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <div className={s.tableHeaderRow}>
            <div className={s.sectionLabel} style={{ marginBottom: 0 }}>
              Payments
              <span className={s.ledgerIntro}>Every movement of money, and the appointment it belongs to</span>
            </div>
          </div>

          {/* Filters on their own line: what to show on the left, when on the right. */}
          <div className={s.filterRow}>
            <div className={s.chipRow}>
                {PAY_FILTERS.map((f) => (
                  <button key={f.id} onClick={() => setFilter(f.id)}
                          className={`${s.chip} ${filter === f.id ? s.chipActive : ''}`}>
                    {f.label}
                  </button>
                ))}
            </div>
            <DateRangeFilter onChange={onRangeChange} />
          </div>

          <div className={s.ledgerTiles}>
            <div className={s.ledgerTile}>
              <div className={s.ledgerTileLabel}>Linked automatically</div>
              <div className={s.ledgerTileValue}>{tiles.linked}</div>
              <div className={s.ledgerTileNote}>at or above 95% confidence</div>
            </div>
            <div className={s.ledgerTile}>
              <div className={s.ledgerTileLabel}>Needs review</div>
              <div className={`${s.ledgerTileValue} ${s.ledgerAmber}`}>{tiles.review}</div>
              <div className={s.ledgerTileNote}>below 95% confidence</div>
            </div>
            <div className={s.ledgerTile}>
              <div className={s.ledgerTileLabel}>Unlinked</div>
              <div className={`${s.ledgerTileValue} ${s.ledgerRed}`}>{tiles.unlinked}</div>
              <div className={s.ledgerTileNote}>
                {money(tiles.unlinkedTips)} in tips with no crew
              </div>
            </div>
            <div className={s.ledgerTile}>
              <div className={s.ledgerTileLabel}>Manual entries</div>
              <div className={`${s.ledgerTileValue} ${s.ledgerAmber}`}>{tiles.manual}</div>
              <div className={s.ledgerTileNote}>{tiles.unverified} awaiting verification</div>
            </div>
            <div className={s.ledgerTile}>
              <div className={s.ledgerTileLabel}>Revenue in range</div>
              <div className={`${s.ledgerTileValue} ${s.ledgerGreen}`}>{money(tiles.revenue)}</div>
              <div className={s.ledgerTileNote}>excludes tax and tip</div>
            </div>
          </div>

          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Transaction Payment Date</th>
                  <th>Customer</th>
                  <th>Location</th>
                  <th>Services</th>
                  <th className={s.num}>Services Subtotal</th>
                  <th className={s.num}>Discount</th>
                  <th className={s.num}>Tax</th>
                  <th className={s.num}>Total B4 Tip</th>
                  <th className={s.num}>Tip</th>
                  <th className={s.num}>Final Total</th>
                  <th>Method</th>
                  <th>Link</th>
                  <th>Assigned</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={13} className={s.loading}>Loading…</td></tr>
                )}
                {!isLoading && visiblePayments.length === 0 && (
                  <tr><td colSpan={13} className={s.empty}>
                    {filter !== 'all' && payments.length > 0
                      ? 'No payments match this filter in the range.'
                      : 'No payments in this range.'}
                  </td></tr>
                )}
                {!isLoading && visiblePayments.map((p) => (
                  <tr key={p.idPayment}>
                    <td>{fmtDateTime(p.createAt)}</td>
                    <td>{p.customerName || p.customerId || '—'}</td>
                    <td>{p.locationName || p.locationId || '—'}</td>
                    <td>
                      {stacked(p.services)}
                      {/* Which appointment this belongs to — matched, proposed,
                          or neither. The manual picker below stays available. */}
                      <div className={`${s.linkChip} ${
                        p.link === 'review'   ? s.chipReview :
                        p.link === 'unlinked' ? s.chipUnlinked : s.chipLinked}`}>
                        {apptChip(p, taxMode)}
                      </div>
                      {isAdmin && (
                        <div style={{ marginTop: 6 }}>
                          <AppointmentSelect row={p} light={lightMode} onChange={handleSetAppointment} />
                        </div>
                      )}
                    </td>
                    <td className={s.num}>{money(p.subTotal)}</td>
                    <td className={s.num}>{money(p.discount)}</td>
                    <td className={s.num}>{money(p.tax)}</td>
                    <td className={s.num}>{money(p.amount)}</td>
                    <td className={s.num}>{money(p.tip)}</td>
                    <td className={s.num}>{money(Math.max(0, p.amount + p.tip))}</td>
                    <td>{methodCell(p)}</td>
                    <td>{linkCell(p)}</td>
                    <td>{stacked(p.teamMembers)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isFetching && !isLoading && <div className={s.loading} style={{ padding: '8px 0' }}>Updating…</div>}
        </div>
      </div>
    </div>
  );
}
